/**
 * MarketPositioner
 *
 * An interactive component for visualizing competitive positioning
 * in the market with comparative analysis of products/companies.
 *
 * Primary responsibilities:
 * - Visualize market positioning on 2D maps
 * - Compare products/companies across multiple dimensions
 * - Show competitive advantages and market gaps
 * - Support strategic positioning analysis
 */

import React, { useState, useMemo } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Slider } from '@/components/ui/slider'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

// Types
export interface Competitor {
  id: string
  name: string
  description: string
  logo?: string
  color?: string
  attributes: Record<string, number> // Attribute name -> value (0-10)
  strengths: string[]
  weaknesses: string[]
  targetSegments: string[]
  marketShare?: number
  revenue?: number
  growth?: number
}

export interface Dimension {
  id: string
  name: string
  description: string
  xLabel?: string
  yLabel?: string
}

export interface PricingTier {
  id: string
  name: string
  price: number
  features: string[]
  competitor: string // Competitor ID
}

export interface MarketSegment {
  id: string
  name: string
  size: number // Market size in percentage (0-100)
  growth: number // Growth rate in percentage
  preferences: Record<string, number> // Attribute name -> importance (0-10)
}

export interface MarketPositionerData {
  title: string
  description: string
  industry: string
  competitors: Competitor[]
  dimensions: Dimension[]
  attributes: string[] // List of available attribute names
  segments?: MarketSegment[]
  pricingTiers?: PricingTier[]
  selectedCompetitors?: string[] // IDs of competitors to highlight
  selectedDimension?: string // ID of currently selected dimension
  viewMode?: 'quadrant' | 'scatter' | 'radar' | 'table'
}

// Utility functions
const getRandomColor = (id: string): string => {
  // Generate deterministic color based on ID string
  let hash = 0
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash)
  }

  let color = '#'
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff
    color += ('00' + value.toString(16)).substr(-2)
  }

  return color
}

const formatPercent = (value: number): string => {
  return `${value.toFixed(1)}%`
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 1
  }).format(value)
}

export const MarketPositioner: React.FC<{ data: MarketPositionerData }> = ({ data }) => {
  const [selectedDimension, setSelectedDimension] = useState<string>(
    data.selectedDimension || (data.dimensions.length > 0 ? data.dimensions[0].id : '')
  )
  const [selectedCompetitors, setSelectedCompetitors] = useState<string[]>(
    data.selectedCompetitors || data.competitors.slice(0, 5).map((c) => c.id)
  )
  const [viewMode, setViewMode] = useState<'quadrant' | 'scatter' | 'radar' | 'table'>(
    data.viewMode || 'quadrant'
  )
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [highlightedCompetitor, setHighlightedCompetitor] = useState<string | null>(null)
  const [selectedAttributes, setSelectedAttributes] = useState<string[]>(
    data.attributes.slice(0, 4)
  )

  // Active dimension
  const activeDimension = useMemo(() => {
    return data.dimensions.find((d) => d.id === selectedDimension) || data.dimensions[0]
  }, [selectedDimension, data.dimensions])

  // Filtered competitors
  const filteredCompetitors = useMemo(() => {
    if (!searchQuery) {
      return data.competitors.filter((c) => selectedCompetitors.includes(c.id))
    }

    const query = searchQuery.toLowerCase()
    return data.competitors.filter(
      (c) =>
        (c.name.toLowerCase().includes(query) || c.description.toLowerCase().includes(query)) &&
        selectedCompetitors.includes(c.id)
    )
  }, [data.competitors, selectedCompetitors, searchQuery])

  // Toggle competitor selection
  const toggleCompetitor = (competitorId: string) => {
    setSelectedCompetitors((prev) =>
      prev.includes(competitorId)
        ? prev.filter((id) => id !== competitorId)
        : [...prev, competitorId]
    )
  }

  // Toggle attribute selection
  const toggleAttribute = (attribute: string) => {
    setSelectedAttributes((prev) =>
      prev.includes(attribute) ? prev.filter((a) => a !== attribute) : [...prev, attribute]
    )
  }

  // Get competitor color
  const getCompetitorColor = (competitor: Competitor): string => {
    return competitor.color || getRandomColor(competitor.id)
  }

  // Render competitor list
  const renderCompetitorList = () => (
    <div className="mb-6">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-lg font-medium">Companies</h3>
        <Input
          className="w-48"
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>
      <div className="flex flex-wrap gap-2">
        {data.competitors.map((competitor) => (
          <Badge
            key={competitor.id}
            variant={selectedCompetitors.includes(competitor.id) ? 'default' : 'outline'}
            className="cursor-pointer"
            style={{
              backgroundColor: selectedCompetitors.includes(competitor.id)
                ? getCompetitorColor(competitor)
                : 'transparent',
              color: selectedCompetitors.includes(competitor.id) ? '#fff' : 'inherit',
              borderColor: getCompetitorColor(competitor)
            }}
            onClick={() => toggleCompetitor(competitor.id)}
          >
            {competitor.name}
          </Badge>
        ))}
      </div>
    </div>
  )

  // Render dimensions list
  const renderDimensionsList = () => (
    <div className="mb-6">
      <h3 className="mb-2 text-lg font-medium">Positioning Dimensions</h3>
      <Select value={selectedDimension} onValueChange={setSelectedDimension}>
        <SelectTrigger className="w-[280px]">
          <SelectValue placeholder="Select dimension" />
        </SelectTrigger>
        <SelectContent>
          {data.dimensions.map((dimension) => (
            <SelectItem key={dimension.id} value={dimension.id}>
              {dimension.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {activeDimension && (
        <p className="mt-2 text-sm text-gray-500">{activeDimension.description}</p>
      )}
    </div>
  )

  // Render view mode selection
  const renderViewModeSelection = () => (
    <div className="mb-6">
      <h3 className="mb-2 text-lg font-medium">View Mode</h3>
      <Tabs defaultValue={viewMode} onValueChange={(value) => setViewMode(value as any)}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="quadrant">Quadrant</TabsTrigger>
          <TabsTrigger value="scatter">Scatter</TabsTrigger>
          <TabsTrigger value="radar">Radar</TabsTrigger>
          <TabsTrigger value="table">Table</TabsTrigger>
        </TabsList>
      </Tabs>
    </div>
  )

  // Render competitor details
  const renderCompetitorDetails = (competitor: Competitor) => (
    <Card key={competitor.id} className="mb-4">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">{competitor.name}</CardTitle>
            {competitor.marketShare && (
              <CardDescription>
                Market Share: {formatPercent(competitor.marketShare)}
                {competitor.growth && ` • Growth: ${formatPercent(competitor.growth)}`}
                {competitor.revenue && ` • Revenue: ${formatCurrency(competitor.revenue)}`}
              </CardDescription>
            )}
          </div>
          <div
            className="size-4 rounded-full"
            style={{ backgroundColor: getCompetitorColor(competitor) }}
          ></div>
        </div>
      </CardHeader>
      <CardContent>
        <p className="mb-4 text-sm text-gray-600">{competitor.description}</p>

        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <h4 className="mb-2 text-sm font-medium">Strengths</h4>
            <ul className="list-inside list-disc space-y-1 text-sm">
              {competitor.strengths.map((strength, i) => (
                <li key={i}>{strength}</li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="mb-2 text-sm font-medium">Weaknesses</h4>
            <ul className="list-inside list-disc space-y-1 text-sm">
              {competitor.weaknesses.map((weakness, i) => (
                <li key={i}>{weakness}</li>
              ))}
            </ul>
          </div>
        </div>

        <h4 className="mb-2 text-sm font-medium">Target Segments</h4>
        <div className="mb-4 flex flex-wrap gap-2">
          {competitor.targetSegments.map((segment, i) => (
            <Badge key={i} variant="outline">
              {segment}
            </Badge>
          ))}
        </div>

        <h4 className="mb-2 text-sm font-medium">Attributes</h4>
        <div className="space-y-3">
          {Object.entries(competitor.attributes)
            .filter(([key]) => selectedAttributes.includes(key))
            .map(([key, value]) => (
              <div key={key}>
                <div className="mb-1 flex justify-between text-sm">
                  <span>{key}</span>
                  <span>{value}/10</span>
                </div>
                <div className="h-2 w-full rounded-full bg-gray-200">
                  <div
                    className="h-2 rounded-full"
                    style={{
                      width: `${value * 10}%`,
                      backgroundColor: getCompetitorColor(competitor)
                    }}
                  ></div>
                </div>
              </div>
            ))}
        </div>
      </CardContent>
    </Card>
  )

  // Render quadrant view
  const renderQuadrantView = () => {
    const xLabel = activeDimension?.xLabel || 'Feature Rich'
    const yLabel = activeDimension?.yLabel || 'Price Premium'

    // Find primary attributes for this dimension
    const xAttribute =
      data.attributes.find((attr) =>
        attr.toLowerCase().includes(xLabel.toLowerCase().split(' ')[0])
      ) || data.attributes[0]

    const yAttribute =
      data.attributes.find((attr) =>
        attr.toLowerCase().includes(yLabel.toLowerCase().split(' ')[0])
      ) || data.attributes[1]

    return (
      <div className="relative aspect-square w-full rounded-md border bg-white p-4">
        {/* Quadrant labels */}
        <div className="absolute left-1/2 top-2 -translate-x-1/2 text-sm font-medium text-gray-500">
          {yLabel} ↑
        </div>
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-sm font-medium text-gray-500">
          {yLabel} ↓
        </div>
        <div className="absolute left-2 top-1/2 -translate-y-1/2 -rotate-90 text-sm font-medium text-gray-500">
          {xLabel} ↓
        </div>
        <div className="absolute right-2 top-1/2 -translate-y-1/2 rotate-90 text-sm font-medium text-gray-500">
          {xLabel} ↑
        </div>

        {/* Axes */}
        <div className="absolute inset-x-0 top-1/2 h-px bg-gray-300"></div>
        <div className="absolute inset-y-0 left-1/2 w-px bg-gray-300"></div>

        {/* Quadrant labels */}
        <div className="absolute left-4 top-4 text-xs text-gray-500">
          High {yLabel}, Low {xLabel}
        </div>
        <div className="absolute right-4 top-4 text-right text-xs text-gray-500">
          High {yLabel}, High {xLabel}
        </div>
        <div className="absolute bottom-4 left-4 text-xs text-gray-500">
          Low {yLabel}, Low {xLabel}
        </div>
        <div className="absolute bottom-4 right-4 text-right text-xs text-gray-500">
          Low {yLabel}, High {xLabel}
        </div>

        {/* Plot competitors */}
        {filteredCompetitors.map((competitor) => {
          const xValue = competitor.attributes[xAttribute] || 5
          const yValue = competitor.attributes[yAttribute] || 5
          const xPosition = `${xValue * 10}%`
          const yPosition = `${100 - yValue * 10}%` // Invert Y-axis
          const color = getCompetitorColor(competitor)

          return (
            <div
              key={competitor.id}
              className="absolute -translate-x-1/2 -translate-y-1/2 cursor-pointer"
              style={{
                left: xPosition,
                top: yPosition
              }}
              onMouseEnter={() => setHighlightedCompetitor(competitor.id)}
              onMouseLeave={() => setHighlightedCompetitor(null)}
            >
              <div
                className={`size-4 rounded-full border-2 transition-all hover:size-6 ${
                  highlightedCompetitor === competitor.id ? 'size-6' : ''
                }`}
                style={{
                  backgroundColor: color,
                  borderColor: 'white'
                }}
              ></div>
              <div
                className={`absolute left-1/2 top-full mt-1 -translate-x-1/2 whitespace-nowrap text-xs font-medium${
                  highlightedCompetitor === competitor.id ? 'block' : 'hidden'
                }`}
              >
                {competitor.name}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  // Render scatter plot
  const renderScatterPlot = () => {
    // Select two attributes for the plot
    const attr1 = selectedAttributes[0] || data.attributes[0]
    const attr2 = selectedAttributes[1] || data.attributes[1]

    return (
      <div className="relative aspect-square w-full rounded-md border bg-white p-4">
        {/* Axis labels */}
        <div className="absolute left-1/2 top-2 -translate-x-1/2 text-sm font-medium text-gray-500">
          {attr2} ↑
        </div>
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-sm font-medium text-gray-500">
          {attr2} ↓
        </div>
        <div className="absolute left-2 top-1/2 -translate-y-1/2 -rotate-90 text-sm font-medium text-gray-500">
          {attr1} ↓
        </div>
        <div className="absolute right-2 top-1/2 -translate-y-1/2 rotate-90 text-sm font-medium text-gray-500">
          {attr1} ↑
        </div>

        {/* Axes */}
        <div className="absolute inset-x-0 top-1/2 h-px bg-gray-300"></div>
        <div className="absolute inset-y-0 left-1/2 w-px bg-gray-300"></div>

        {/* Value grid */}
        <div className="absolute inset-0 grid grid-cols-10 grid-rows-10">
          {Array.from({ length: 9 }).map((_, i) => (
            <div
              key={`x-${i}`}
              className="absolute inset-x-0 h-px bg-gray-100"
              style={{ top: `${(i + 1) * 10}%` }}
            ></div>
          ))}
          {Array.from({ length: 9 }).map((_, i) => (
            <div
              key={`y-${i}`}
              className="absolute inset-y-0 w-px bg-gray-100"
              style={{ left: `${(i + 1) * 10}%` }}
            ></div>
          ))}
        </div>

        {/* Plot competitors */}
        {filteredCompetitors.map((competitor) => {
          const xValue = competitor.attributes[attr1] || 5
          const yValue = competitor.attributes[attr2] || 5
          const xPosition = `${xValue * 10}%`
          const yPosition = `${100 - yValue * 10}%` // Invert Y-axis
          const bubbleSize = competitor.marketShare
            ? Math.max(20, Math.min(60, competitor.marketShare / 2))
            : 20
          const color = getCompetitorColor(competitor)

          return (
            <div
              key={competitor.id}
              className="absolute -translate-x-1/2 -translate-y-1/2 cursor-pointer"
              style={{
                left: xPosition,
                top: yPosition
              }}
              onMouseEnter={() => setHighlightedCompetitor(competitor.id)}
              onMouseLeave={() => setHighlightedCompetitor(null)}
            >
              <div
                className="rounded-full border border-white transition-all"
                style={{
                  width: `${bubbleSize}px`,
                  height: `${bubbleSize}px`,
                  backgroundColor: `${color}80`, // Add transparency
                  transform: highlightedCompetitor === competitor.id ? 'scale(1.2)' : 'scale(1)'
                }}
              ></div>
              <div className="absolute left-1/2 top-full mt-1 -translate-x-1/2 whitespace-nowrap text-xs font-medium">
                {competitor.name}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  // Render radar chart (simplified version)
  const renderRadarChart = () => {
    const selectedAttrs =
      selectedAttributes.length >= 3 ? selectedAttributes.slice(0, 6) : data.attributes.slice(0, 6)

    const sides = selectedAttrs.length
    const angleStep = (Math.PI * 2) / sides

    return (
      <div className="relative aspect-square w-full rounded-md border bg-white p-4">
        <svg width="100%" height="100%" viewBox="-10 -10 120 120" className="overflow-visible">
          {/* Background polygons/circles */}
          {[2, 4, 6, 8, 10].map((level) => (
            <polygon
              key={`level-${level}`}
              points={Array.from({ length: sides })
                .map((_, i) => {
                  const angle = i * angleStep - Math.PI / 2
                  const r = level * 5 // Scale to 0-50
                  return `${50 + r * Math.cos(angle)},${50 + r * Math.sin(angle)}`
                })
                .join(' ')}
              fill="none"
              stroke="#e5e7eb"
              strokeWidth="0.5"
            />
          ))}

          {/* Axis lines */}
          {selectedAttrs.map((_, i) => {
            const angle = i * angleStep - Math.PI / 2
            return (
              <line
                key={`axis-${i}`}
                x1="50"
                y1="50"
                x2={50 + 50 * Math.cos(angle)}
                y2={50 + 50 * Math.sin(angle)}
                stroke="#d1d5db"
                strokeWidth="0.5"
              />
            )
          })}

          {/* Axis labels */}
          {selectedAttrs.map((attr, i) => {
            const angle = i * angleStep - Math.PI / 2
            const r = 56 // Label position just outside the chart
            return (
              <text
                key={`label-${i}`}
                x={50 + r * Math.cos(angle)}
                y={50 + r * Math.sin(angle)}
                fontSize="8"
                textAnchor="middle"
                dominantBaseline="middle"
                fill="#6b7280"
              >
                {attr}
              </text>
            )
          })}

          {/* Competitor polygons */}
          {filteredCompetitors.map((competitor) => {
            const color = getCompetitorColor(competitor)

            // Create polygon points
            const points = selectedAttrs
              .map((attr, i) => {
                const angle = i * angleStep - Math.PI / 2
                const value = competitor.attributes[attr] || 0
                const r = value * 5 // Scale to 0-50
                return `${50 + r * Math.cos(angle)},${50 + r * Math.sin(angle)}`
              })
              .join(' ')

            return (
              <g key={competitor.id}>
                <polygon
                  points={points}
                  fill={`${color}40`} // Very transparent
                  stroke={color}
                  strokeWidth={highlightedCompetitor === competitor.id ? '2' : '1'}
                  onMouseEnter={() => setHighlightedCompetitor(competitor.id)}
                  onMouseLeave={() => setHighlightedCompetitor(null)}
                />
              </g>
            )
          })}

          {/* Legend */}
          <foreignObject x="-10" y="90" width="120" height="30">
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
              {filteredCompetitors.map((competitor) => (
                <div key={competitor.id} className="flex items-center">
                  <div
                    className="mr-1 size-3 rounded-full"
                    style={{ backgroundColor: getCompetitorColor(competitor) }}
                  ></div>
                  <span>{competitor.name}</span>
                </div>
              ))}
            </div>
          </foreignObject>
        </svg>
      </div>
    )
  }

  // Render comparison table
  const renderComparisonTable = () => {
    return (
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Company</TableHead>
              {selectedAttributes.map((attr) => (
                <TableHead key={attr}>{attr}</TableHead>
              ))}
              {data.segments && <TableHead>Target Segments</TableHead>}
              {data.competitors[0].marketShare !== undefined && <TableHead>Market Share</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredCompetitors.map((competitor) => (
              <TableRow key={competitor.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div
                      className="size-3 rounded-full"
                      style={{ backgroundColor: getCompetitorColor(competitor) }}
                    ></div>
                    <span>{competitor.name}</span>
                  </div>
                </TableCell>

                {selectedAttributes.map((attr) => (
                  <TableCell key={attr}>
                    <div className="mb-1 h-2 w-full rounded-full bg-gray-200">
                      <div
                        className="h-2 rounded-full"
                        style={{
                          width: `${(competitor.attributes[attr] || 0) * 10}%`,
                          backgroundColor: getCompetitorColor(competitor)
                        }}
                      ></div>
                    </div>
                    <div className="text-center text-xs">{competitor.attributes[attr] || 0}/10</div>
                  </TableCell>
                ))}

                {data.segments && (
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {competitor.targetSegments.slice(0, 2).map((segment, i) => (
                        <Badge key={i} variant="outline" className="text-xs">
                          {segment}
                        </Badge>
                      ))}
                      {competitor.targetSegments.length > 2 && (
                        <Badge variant="outline" className="text-xs">
                          +{competitor.targetSegments.length - 2}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                )}

                {competitor.marketShare !== undefined && (
                  <TableCell>{formatPercent(competitor.marketShare)}</TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    )
  }

  // Render attribute selection
  const renderAttributeSelection = () => (
    <div className="mb-6">
      <h3 className="mb-2 text-lg font-medium">Key Attributes</h3>
      <div className="flex flex-wrap gap-2">
        {data.attributes.map((attribute) => (
          <Badge
            key={attribute}
            variant={selectedAttributes.includes(attribute) ? 'default' : 'outline'}
            className="cursor-pointer"
            onClick={() => toggleAttribute(attribute)}
          >
            {attribute}
          </Badge>
        ))}
      </div>
    </div>
  )

  return (
    <div className="market-positioner my-4 rounded-lg border bg-white p-4">
      <div className="mb-6">
        <h2 className="text-2xl font-bold">{data.title}</h2>
        <p className="text-gray-600">{data.description}</p>
        {data.industry && (
          <Badge variant="outline" className="mt-2">
            {data.industry}
          </Badge>
        )}
      </div>

      {/* Competitor selection */}
      {renderCompetitorList()}

      {/* Attribute selection */}
      {renderAttributeSelection()}

      {/* Dimension selection & view mode */}
      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2">
        {renderDimensionsList()}
        {renderViewModeSelection()}
      </div>

      {/* Visualization based on view mode */}
      <div className="mb-6">
        {viewMode === 'quadrant' && renderQuadrantView()}
        {viewMode === 'scatter' && renderScatterPlot()}
        {viewMode === 'radar' && renderRadarChart()}
        {viewMode === 'table' && renderComparisonTable()}
      </div>

      {/* Competitor details */}
      <div className="mt-8">
        <h3 className="mb-4 text-lg font-medium">Company Details</h3>
        <div className="space-y-4">
          {filteredCompetitors.map((competitor) => renderCompetitorDetails(competitor))}
        </div>
      </div>
    </div>
  )
}

export default MarketPositioner
